import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { categories, comics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

const categoryMap = new Map<string, string>();
categories.forEach((c) => categoryMap.set(c.slug, c.name));

const sortOptions = [
  { value: 'latest', label: '最新更新' },
  { value: 'popular', label: '最受欢迎' },
  { value: 'rating', label: '评分最高' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState('latest');
  const [activeCategory, setActiveCategory] = useState(slug || 'rexue-dongzuo');

  const categoryName = categoryMap.get(activeCategory) || '热血动作';

  const filteredComics = useMemo(() => {
    let result = comics.filter((c) => c.categorySlug === activeCategory);
    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else {
      result = [...result].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
    return result;
  }, [activeCategory, sortBy]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const categorySchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${categoryName} - 韩国漫画分类 - 51漫画网`,
      description: `51漫画网${categoryName}分类，提供高质量无删减韩国漫画下拉式阅读，收录${filteredComics.length}部${categoryName}题材韩漫，免费畅读。`,
      url: `https://www.51manhua.com/category/${activeCategory}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '51漫画网',
        url: 'https://www.51manhua.com',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://www.51manhua.com',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '分类浏览',
          item: 'https://www.51manhua.com/category/rexue-dongzuo',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: categoryName,
        },
      ],
    },
  ];

  return (
    <>
      <SEO
        title={`${categoryName} - 韩国漫画免费阅读 - 51漫画网`}
        description={`51漫画网${categoryName}分类，提供高质量无删减韩国漫画下拉式阅读，收录${filteredComics.length}部${categoryName}题材韩漫，免费畅读，更新及时。`}
        keywords={`${categoryName},韩国漫画,无删减漫画,下拉式漫画,51漫画网,韩漫`}
        canonicalUrl={`https://www.51manhua.com/category/${activeCategory}`}
        schemaData={categorySchema}
      />

      <Navbar />
      <ScrollToTop />

      <main className="pt-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '分类浏览', to: '/category/rexue-dongzuo' },
              { label: categoryName },
            ]}
          />

          <div className="mt-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 <strong className="text-primary-500">{filteredComics.length}</strong> 部韩国漫画
            </p>
          </div>

          {/* Category Tabs */}
          <div className="mt-8 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                type="button"
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Sort Options */}
          <div className="mt-6 flex items-center justify-between">
            <p className="text-xs text-foreground-400">
              找到 {filteredComics.length} 个结果
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSortBy(opt.value)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    sortBy === opt.value
                      ? 'bg-background-50 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Comic Grid */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
            {filteredComics.map((comic: Comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>

          {filteredComics.length === 0 && (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-book-2-line text-foreground-300 text-3xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无漫画</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}